// 📄 components/Configuracion/ApiDefinitionCard.tsx
// Fila de "APIs configuradas": header con icono/nombre/badges (ambiente,
// credenciales)/Editar/Eliminar/chevron, más el formulario
// (ApiDefinitionForm.tsx) cuando está expandida. Dos consumidores con dos
// patrones de edición distintos:
//   - ConfiguracionApis.tsx (APIs del proyecto): pasa `onEditar` — el
//     botón "Editar" abre NuevaApiModal.tsx en modo edición (formulario
//     largo en su propio modal, con el botón de guardar siempre a la
//     vista, sin scroll de card en card) y la card expandida queda
//     siempre de solo lectura.
//   - AribaApiTester.tsx ("Mis APIs" personales, sin cliente/proyecto):
//     sigue con el patrón legacy `editable`/`onToggleEditable`/`onChange`
//     — edición inline dentro de la misma card. No es parte de este
//     cambio, se mantiene tal cual para no romper ese flujo.
// Si viene `onEditar`, ese modo gana; si no, cae al patrón legacy.
import { Trash2, ChevronDown, ChevronUp, Pencil, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { ApiDefinition } from "../../services/clienteConexionService.ts";
import { ApiTemplate } from "../../services/catalogoApisService.ts";
import { TIPOS_API } from "./apiTiposConfig.ts";
import ApiDefinitionForm from "./ApiDefinitionForm.tsx";

interface ApiDefinitionCardProps {
  api: ApiDefinition;
  expandida: boolean;
  catalogo: ApiTemplate[];
  onToggleExpandida: () => void;
  onRemove: () => void;
  // Patrón nuevo (ConfiguracionApis.tsx) — "Editar" abre un modal aparte.
  onEditar?: () => void;
  // Patrón legacy (AribaApiTester.tsx) — edición inline en la misma card.
  editable?: boolean;
  onToggleEditable?: () => void;
  onChange?: (api: ApiDefinition) => void;
  // Flujo de aprobación para obtener sus credenciales (solo APIs del cliente
  // con credenciales guardadas, ver ConfiguracionApis.tsx).
  onAprobacion?: () => void;
}

export default function ApiDefinitionCard({ api, expandida, catalogo, onToggleExpandida, onRemove, onEditar, editable, onToggleEditable, onChange, onAprobacion }: ApiDefinitionCardProps) {
  const tipoMeta = TIPOS_API[api.tipo] || TIPOS_API.ariba;
  // Con onEditar, la card expandida siempre es de solo lectura (editar de
  // verdad pasa por el modal); sin onEditar, cae al patrón legacy donde
  // `editable` sí habilita los campos inline.
  const editableInline = !onEditar && !!editable;

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 p-5">
        <button
          type="button"
          onClick={onToggleExpandida}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
        >
          <span className="p-2 bg-blue-500/20 rounded-xl text-blue-400 flex-shrink-0 text-base leading-none flex items-center justify-center w-8 h-8">
            {tipoMeta.icon}
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-white truncate">{api.nombre || "API sin nombre"}</h3>
            <p className="text-xs text-gray-500 truncate">{tipoMeta.label} · {api.urlBase || "Sin URL Base configurada"}</p>
          </div>
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          {api.ambiente && (
            <span
              className={`hidden sm:flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${
                api.ambiente === "PRD"
                  ? "text-red-400 bg-red-500/10 border-red-500/30"
                  : "text-sky-400 bg-sky-500/10 border-sky-500/30"
              }`}
            >
              {api.ambiente}
            </span>
          )}
          {api.tieneCredencialesGuardadas ? (
            <span className="hidden sm:flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2 py-1 rounded-full">
              <CheckCircle2 className="w-3 h-3" /> Configurada
            </span>
          ) : (
            <span className="hidden sm:flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded-full">
              <AlertTriangle className="w-3 h-3" /> Sin credenciales
            </span>
          )}

          {onAprobacion && api.tieneCredencialesGuardadas && (
            <button
              type="button"
              onClick={onAprobacion}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              title="Quién aprueba si alguien pide estas credenciales"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Aprobación
            </button>
          )}

          <button
            type="button"
            onClick={onEditar || onToggleEditable}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              editableInline ? "bg-blue-600/20 text-blue-300" : "text-gray-400 hover:text-white hover:bg-white/10"
            }`}
            title={editableInline ? "Bloquear edición" : "Editar esta API"}
          >
            <Pencil className="w-3.5 h-3.5" /> {editableInline ? "Editando" : "Editar"}
          </button>

          <button
            type="button"
            onClick={onRemove}
            className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Eliminar API"
          >
            <Trash2 className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={onToggleExpandida}
            className="p-2 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title={expandida ? "Minimizar" : "Expandir"}
          >
            {expandida ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expandida && (
        <div className="px-5 pb-5">
          <ApiDefinitionForm api={api} editable={editableInline} catalogo={catalogo} onChange={onChange || (() => {})} />
        </div>
      )}
    </div>
  );
}
